// ============================================================================
//  VisitDateSelect — pick the day of the visit a claim row belongs to
// ----------------------------------------------------------------------------
//  One control, used by the three row tables that carry a date: medications
//  (`dispensed_at`), procedures and investigations (`performed_at`). They all ask
//  the same question — which day of this stay was this on — and the answer is
//  bounded by the same list, from /claims/store/procedure/dates: the one visit day
//  for an out-patient claim, every day of the stay for an admission.
//
//  WHY IT EXISTS
//
//  All three tables already had a dropdown on the DRAFT row and plain text on the
//  SAVED ones. So changing the day of a drug already on the claim meant opening
//  the whole row for editing, finding the date among eight other fields, changing
//  it and saving — for a value that has at most a handful of legal answers. On a
//  four-night admission with ten drugs that is the commonest correction there is.
//
//  THE OUT-OF-RANGE CASE, which is the reason this is a component and not three
//  copies of a <select>
//
//  A <select> whose `value` matches none of its <option>s does not show that
//  value. It shows the first option, silently — so a row dated outside the
//  computed stay would render as though it were on the first day of the visit, and
//  an officer reading the table would be told something untrue about the claim.
//
//  Imported claims do carry such rows: a medication dispensed the day after
//  discharge, a date the importer took verbatim. So a value that is not in the
//  list is KEPT as an option of its own and marked, rather than being dropped or
//  quietly corrected. Choosing another day is still allowed — that is how it gets
//  fixed — but nothing is changed by merely rendering the row.
// ============================================================================

import React from 'react';

const dmy = (s) => {
  if (!s) return '';
  const [y, m, d] = String(s).slice(0, 10).split('-');
  return y && m && d ? `${d}/${m}/${y}` : String(s);
};

//  Both sides normalised to YYYY-MM-DD. `dates` comes back as a plain date string
//  and the column is a DATETIME, so the row's value arrives as
//  "2026-07-01 00:00:00" and would never equal "2026-07-01" untrimmed.
export const dayOf = (v) => String(v == null ? '' : v).slice(0, 10);

export default function VisitDateSelect({
  value,
  dates = [],
  onChange,
  disabled = false,
  busy = false,
  ariaLabel = 'Date',
  style,
}) {
  const current = dayOf(value);
  const known = (dates || []).map(dayOf).filter(Boolean);
  const outOfRange = Boolean(current) && !known.includes(current);

  //  Nothing to choose between: one legal day, or none known. Rendering a dropdown
  //  with a single option invites a click that cannot change anything.
  if (!known.length || (known.length === 1 && !outOfRange)) {
    return <span style={{ color: '#475569', ...style }}>{dmy(current || known[0])}</span>;
  }

  //  Read-only, but still worth showing which day — just not as a control.
  if (disabled) {
    return (
      <span style={{ color: '#475569', ...style }}>
        {dmy(current)}
        {outOfRange && <OutOfRange />}
      </span>
    );
  }

  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, minWidth: 0 }}>
      <select
        aria-label={ariaLabel}
        value={current}
        disabled={busy}
        onChange={(e) => {
          const next = e.target.value;
          if (next && next !== current && onChange) onChange(next);
        }}
        style={{
          padding: '3px 6px', fontSize: 12, borderRadius: 6, fontFamily: 'inherit',
          border: `1px solid ${outOfRange ? '#fcd34d' : '#e2e8f0'}`,
          background: busy ? '#f8fafc' : (outOfRange ? '#fffbeb' : '#fff'),
          color: '#0f172a', cursor: busy ? 'progress' : 'pointer',
          ...style,
        }}>
        {/*  The row's own day first when it is not a day of the visit, so the
             select shows what the claim actually says instead of substituting the
             first legal option for it. */}
        {outOfRange && (
          <option value={current}>{dmy(current)} — outside the visit</option>
        )}
        {known.map((d) => <option key={d} value={d}>{dmy(d)}</option>)}
      </select>
      {busy && (
        <span aria-hidden="true" style={{ fontSize: 10, color: '#94a3b8' }}>…</span>
      )}
    </span>
  );
}

function OutOfRange() {
  return (
    <span
      title="This date is not one of the days of this visit"
      style={{
        marginLeft: 5, padding: '1px 5px', borderRadius: 4, fontSize: 9,
        fontWeight: 800, background: '#fffbeb', color: '#92400e',
        border: '1px dashed #fcd34d',
      }}>
      OUTSIDE THE VISIT
    </span>
  );
}
